import React, { useEffect, useState } from 'react';
import { InputNumber, Modal, Radio, Space, notification } from 'antd';
import pmApi from './pmApi';
import { PRICING_MODE_ORDER, PRICING_MODE_LABEL, pricingModeSummary } from '../../components/PlanManagement/premiumTypes';

export const fmtMoney = (v) => `₦${Number(v || 0).toLocaleString('en-NG', { maximumFractionDigits: 0 })}`;

/**
 * Set or change the price of one band on one plan. Shared by the general Bands
 * & Pricing tab and the plan-level view opened from the Plans Catalogue, so
 * both state the same consequence and accept the same pricing natures
 * (dependent categories: fixed / % of Individual / same as Individual).
 *
 * @param {{band, current}|null} target  band = pm_bands row; current = the
 *        plan's existing pm_plan_bands row, or null when attaching.
 */
export default function PlanBandPriceModal({ planId, target, onClose, onSaved }) {
  const [priceDraft, setPriceDraft] = useState(null);
  const [modeDraft, setModeDraft] = useState('fixed');
  const [percentDraft, setPercentDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!target) return;
    const { current } = target;
    setPriceDraft(current ? Number(current.current_price) : null);
    setModeDraft(current?.pricing_mode || 'fixed');
    setPercentDraft(current?.pricing_percent != null ? Number(current.pricing_percent) : null);
  }, [target]);

  const isDependent = target?.band?.premium_type === 'dependent';

  const save = async () => {
    const mode = isDependent ? modeDraft : 'fixed';
    if (mode === 'fixed' && (!priceDraft || priceDraft <= 0)) {
      notification.warning({ message: 'Enter a positive price' });
      return;
    }
    if (mode === 'percent_of_individual' && (!percentDraft || percentDraft <= 0)) {
      notification.warning({ message: 'Enter the percentage of the Individual price' });
      return;
    }
    setSaving(true);
    const res = await pmApi.setPrice(planId, target.band.id, {
      price: priceDraft || 0,
      pricing_mode: mode,
      pricing_percent: percentDraft,
    });
    setSaving(false);
    if (res.ok) {
      notification.success({ message: target.current ? 'Price changed' : 'Band added and priced', description: res.message });
      onSaved?.(res);
      onClose?.();
    }
  };

  return (
    <Modal
      open={!!target}
      title={<span>{target?.current ? 'Change price' : 'Add to plan & set price'} — {target?.band?.name}</span>}
      onCancel={onClose}
      onOk={save}
      okText={target?.current ? 'Apply' : 'Add & price'}
      confirmLoading={saving}
      destroyOnClose
    >
      <Space direction="vertical" style={{ width: '100%' }} size={10}>
        {target?.current ? (
          <div className="pm-rail-row">
            <span>Current</span>
            <b>
              {fmtMoney(target.current.effective_price ?? target.current.current_price)}
              {target.current.pricing_mode && target.current.pricing_mode !== 'fixed' ? ` (${pricingModeSummary(target.current)})` : ''}
            </b>
          </div>
        ) : null}
        {isDependent ? (
          <div>
            <div className="pm-benefit-sub" style={{ marginBottom: 4 }}>Pricing nature for this category on this plan</div>
            <Radio.Group
              value={modeDraft}
              onChange={(e) => setModeDraft(e.target.value)}
              optionType="button"
              buttonStyle="solid"
              size="small"
              options={PRICING_MODE_ORDER.map((m) => ({ value: m, label: PRICING_MODE_LABEL[m] }))}
            />
          </div>
        ) : null}
        {isDependent && modeDraft === 'percent_of_individual' ? (
          <InputNumber
            style={{ width: '100%' }}
            value={percentDraft}
            onChange={setPercentDraft}
            min={1}
            max={1000}
            addonAfter="% of the Individual price"
            placeholder="e.g. 50"
          />
        ) : null}
        {modeDraft === 'fixed' || !isDependent ? (
          <InputNumber
            style={{ width: '100%' }}
            value={priceDraft}
            onChange={setPriceDraft}
            min={1}
            addonBefore="₦"
            formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            parser={(v) => `${v}`.replace(/,/g, '')}
            placeholder="New price"
          />
        ) : null}
        {isDependent && modeDraft === 'same_as_individual' ? (
          <div className="pm-benefit-sub">
            This category will always cost exactly the plan&apos;s Individual price, following it when that changes.
          </div>
        ) : null}
        <div className="pm-consequence">
          {target?.current
            ? 'This closes the current history period and opens a new one. '
            : 'This attaches the band to the plan and opens its first price period. '}
          <b>It changes no pricing event</b> — clients on this band keep the amount
          they paid; renewals from today are charged the applicable price then
          (org-negotiated overrides win where present).
        </div>
      </Space>
    </Modal>
  );
}
